import { Button } from '../ui/Button';
import { useTranslation } from '../../../lib/i18n';
import { logPageClickBi } from '../../../lib/bi-logger';
import { CHAT_HOME_BI } from './message-utils';
import { connectorLogoUrl } from '../../../lib/connectors';
import { useSuggestedTasks, type SuggestedTask } from '../../../lib/suggested-tasks';

/**
 * The suggested-task chips under an empty composer — the genome's answer to "what could I ask for?",
 * personalized per business and refreshed on every genome recalc.
 *
 * <p>Three rules the design turns on:
 *
 * 1. **A tap PREFILLS, it does not send** — and hovering first SHOWS what would be sent. These are
 *    the assistant's guesses, and a guess that acts on its own is one the user cannot decline. The
 *    reveal is the preview; the composer keeps the last word.
 * 2. **The row is silent when it has nothing.** Loading, failed, or none authored all render null.
 *    The composer below is the feature; a spinner or an error where six optional buttons would be is
 *    strictly worse than the buttons never appearing.
 * 3. **A connect chip says so on its face.** At most one chip names an app the business has not
 *    connected; it carries a visible marker, not just a tooltip, because a chip that silently leads
 *    to a connect wall spends the user's highest-intent moment on a dead end.
 *
 * **Written-down parity exception — the reveal is hover-only, so touch web does not get it.** iOS and
 * Android long-press for the same text; on the web the reveal is the native `title` tooltip, which has
 * no touch equivalent. A mobile-web user can still read the prompt — it lands in the composer, above
 * the keyboard, before they send — so the information is not lost, only its preview. Fixing it properly
 * means a bespoke press-and-hold popover, which is more machinery than the gap justifies today; revisit
 * if mobile web becomes a primary chat surface. (Repo golden rule: an exception is written down, never
 * a silent gap.)
 */
export function SuggestedTaskChips({
  enabled,
  narrow,
  onPick,
}: {
  /** Only fetch when the empty landing is actually showing — no chips, no request. */
  enabled: boolean;
  narrow: boolean;
  /** Put this text in the composer. The caller focuses the input; it must NOT send. */
  onPick: (prompt: string) => void;
}) {
  const { t } = useTranslation();
  const state = useSuggestedTasks(enabled);
  if (state.status !== 'ready') return null;

  return (
    <div style={{ ...WRAP, padding: narrow ? '0 16px 20px' : '0 48px 24px' }}>
      {state.tasks.map((task, i) => (
        <Chip key={`${task.label}-${i}`} task={task} connectLabel={t('common.connect')} onPick={onPick} />
      ))}
    </div>
  );
}

function Chip({
  task,
  connectLabel,
  onPick,
}: {
  task: SuggestedTask;
  connectLabel: string;
  onPick: (prompt: string) => void;
}) {
  // The reveal shows the PROMPT — the exact text a tap puts in the composer — so the user can read
  // what they are about to send before they send it. (`why` is the internal rationale; it rides the
  // accessibility label below, where length is free, rather than competing for the tooltip.) A connect
  // chip is prefixed with its state, the more urgent thing to know before tapping.
  const tooltip = task.requiresConnect ? `${connectLabel} · ${task.prompt}` : task.prompt;
  return (
    <Button
      variant="outlined"
      size="small"
      title={tooltip}
      // Screen readers get the rationale too — no real estate constraint here, unlike the chip.
      aria-label={task.why ? `${task.label} — ${task.why}` : task.label}
      onClick={() => {
        // Which chip was picked, not merely that one was — parity with iOS/Android, which both log this.
        // Without it, any read on which suggestions earn their place is blind to desktop.
        logPageClickBi({
          ...CHAT_HOME_BI,
          button_name: 'suggested_task_selected',
          location: 'composer',
        });
        onPick(task.prompt);
      }}
      style={{ maxWidth: '100%', opacity: task.requiresConnect ? 0.72 : 1 }}
      startIcon={
        task.connector ? (
          <img
            src={connectorLogoUrl(task.connector)}
            alt=""
            aria-hidden
            width={14}
            height={14}
            style={{ width: 14, height: 14, borderRadius: 3, flexShrink: 0 }}
            // A logo CDN that 404s a slug must cost the chip its picture, not its text.
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : undefined
      }
      endIcon={task.requiresConnect ? <span style={CONNECT_MARK}>{connectLabel}</span> : undefined}
    >
      <span style={LABEL}>{task.label}</span>
    </Button>
  );
}

const WRAP: React.CSSProperties = {
  width: '100%',
  maxWidth: 760,
  alignSelf: 'center',
  boxSizing: 'border-box',
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: 8,
};

const LABEL: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const CONNECT_MARK: React.CSSProperties = {
  fontSize: 11,
  color: 'rgba(var(--ink-rgb), 0.45)',
  border: '1px solid rgba(var(--hairline-rgb), 0.5)',
  borderRadius: 999,
  padding: '1px 6px',
  flexShrink: 0,
};
